import * as RadixDialog from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/cn";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";
import { DIALOG_MOTION, OVERLAY_MOTION } from "@/lib/motion";

/**
 * 모달. Radix Dialog 위에 시안의 생김새만 입혔다.
 *
 * 한 파일에 Root/Trigger/Content가 함께 있는 건 react-component-rules §7의
 * 합성(슬롯) 패턴이다. `<Dialog.Content>`처럼 쓴다.
 * 포커스 트랩·Esc 닫기·스크롤 잠금은 Radix가 처리한다(§10).
 */

/**
 * 모달 본체.
 *
 * **자리 잡는 일을 스스로 하지 않는다.** 예전에는
 * `fixed top-1/2 left-1/2 -translate-x-1/2`로 직접 가운데 섰는데, 이 방식은
 * 두 군데서 깨진다.
 *
 * 하나, `position: fixed`는 조상에 `transform`이 하나라도 있으면 화면이 아니라
 * **그 조상**을 기준으로 잡는다. 좁은 화면용 서랍이 `translate`로 움직이므로
 * 앱 어딘가에 늘 transform이 있다.
 *
 * 둘, 폭을 `100vw`로 재면 그건 눈에 보이는 폭이 아니라 레이아웃 뷰포트다.
 * 가로로 넘치는 것이 있거나 화면이 확대돼 있으면 실제보다 커져서, 가운데 선
 * 모달이 양옆으로 삐져나가 잘린다.
 *
 * 그래서 아래 `DialogContent`가 화면을 꽉 채우는 flex 상자로 감싸고, 본체는
 * 그 안에서 그냥 가운데 놓인다. 계산이 없으니 어긋날 것도 없다.
 */
const content = cva(
  "flex max-h-full w-full flex-col overflow-hidden bg-surface shadow-modal focus:outline-none",
  {
    variants: {
      size: {
        /** 프로필 — 목록 패널과 같은 폭 */
        sm: "rounded-[20px] sm:max-w-94",
        /** 새 채팅 */
        md: "rounded-[18px] sm:max-w-130",
      },
    },
    defaultVariants: { size: "sm" },
  },
);

function DialogContent({
  size,
  className,
  children,
  ...rest
}: VariantProps<typeof content> &
  ComponentPropsWithoutRef<typeof RadixDialog.Content>) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        className={`fixed inset-0 z-50 bg-[rgb(23_21_15_/_0.34)] ${OVERLAY_MOTION}`}
      />
      {/*
        화면을 꽉 채우고 그 안에서 가운데 놓는 상자. 여기가 자리를 잡으므로
        본체는 폭과 생김새만 신경 쓰면 된다.

        `pointer-events-none`인 이유: 이 상자가 화면 전체를 덮고 있어서, 이게
        클릭을 먹으면 뒤에 깔린 덮개(바깥을 눌러 닫는 자리)에 손이 닿지 않는다.
        본체만 다시 켠다.
      */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <RadixDialog.Content
          className={cn(
            "pointer-events-auto",
            content({ size }),
            DIALOG_MOTION,
            className,
          )}
          {...rest}
        >
          {children}
        </RadixDialog.Content>
      </div>
    </RadixDialog.Portal>
  );
}

/**
 * 닫는 것. 그냥 두면 우상단 ✕이고, `asChild`로 감싸면 **감싼 것이 닫는 단추**가 된다.
 *
 * ⚠️ 예전에는 `asChild`를 무시하고 늘 ✕를 그렸다. 그래서
 * `<Dialog.Close asChild><Button>취소</Button></Dialog.Close>`가 취소 단추 대신
 * ✕ 아이콘 하나로 바뀌었고, **FormDialog를 쓰는 창 전부에 취소가 없었다**
 * (이름 바꾸기·비밀번호·주소 늘리기·프로필 사진). 창이 ✕와 Esc로 닫히긴 해서
 * 눈에 잘 안 띄었다.
 */
function DialogClose({
  className,
  asChild,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof RadixDialog.Close>) {
  // 남의 모양을 빌려 쓰는 경우다. 우리 자리·색을 얹으면 그 모양을 망가뜨린다.
  if (asChild) {
    return (
      <RadixDialog.Close asChild className={className} {...rest}>
        {children}
      </RadixDialog.Close>
    );
  }

  return (
    <RadixDialog.Close
      aria-label="닫기"
      className={cn(
        // Radix가 창을 열면서 대개 이 버튼에 초점을 준다. 테두리가 없으면
        // 키보드로 연 사람은 **처음부터** 자기가 어디 있는지 모른다.
        `absolute top-4 right-4.5 z-10 rounded p-1 text-muted-3 hover:text-body active:text-ink ${PRESS_FLAT} ${FOCUS}`,
        className,
      )}
      {...rest}
    >
      <X className="size-4" />
    </RadixDialog.Close>
  );
}

function DialogTitle({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof RadixDialog.Title>) {
  return (
    <RadixDialog.Title
      className={cn("text-[19px] font-bold tracking-tight text-ink", className)}
      {...rest}
    />
  );
}

function DialogDescription({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof RadixDialog.Description>) {
  return (
    <RadixDialog.Description
      className={cn("text-[13px] text-muted-2", className)}
      {...rest}
    />
  );
}

export const Dialog = Object.assign(RadixDialog.Root, {
  Trigger: RadixDialog.Trigger,
  Content: DialogContent,
  Title: DialogTitle,
  Description: DialogDescription,
  Close: DialogClose,
  /** 화면에 제목을 보이고 싶지 않을 때 Title을 숨기는 래퍼 */
  HiddenTitle: RadixDialog.Title,
});
